import {PayloadAction, createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {SupportContactFormService} from "webform/gen/supportContactForm";
import {ErrorType} from "config/errors";
import {supportWebformApi} from "webform/api";

export interface AddAttachmentMultipartWithFilesRequest {
    file: File;
}

export const addAttachment = createAsyncThunk(
    "webform/form/addAttachment",
    async (req: AddAttachmentMultipartWithFilesRequest, {rejectWithValue}) => {
        try {
            return await supportWebformApi.addAttachment(req, {});
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);

export const deleteAttachmentAction = createAsyncThunk(
    "webform/form/deleteAttachment",
    async (req: SupportContactFormService.DeleteAttachmentRequest, {rejectWithValue}) => {
        try {
            return await supportWebformApi.deleteAttachment(req);
        } catch (err) {
            return rejectWithValue(err);
        }
    },
);

interface UploadedFile {
    id?: number;
    error?: ErrorType;
    isUploaded: boolean;
    file: File;
}

interface SubmitFormState {
    attachments: UploadedFile[];
}

export const initialState: SubmitFormState = {
    attachments: [],
};

export const slice = createSlice({
    name: "attachments",
    initialState,
    reducers: {
        removeFailedAttachment: (state, action: PayloadAction<File>) => {
            state.attachments = state.attachments.filter((file) => file.file !== action.payload);
        },
        registerAttachments: (state, action: PayloadAction<File[]>) => {
            action.payload.forEach((file) => {
                state.attachments.push({file, isUploaded: false, error: undefined});
            });
        },
    },
    extraReducers: (builder) => {
        // addAttachment
        builder.addCase(addAttachment.fulfilled, (state, action) => {
            const file = state.attachments.find((attachment) => attachment.file === action.meta.arg.file);
            if (file) {
                file.error = undefined;
                file.isUploaded = true;
                file.id = action.payload.attachment_id;
            }
        });
        builder.addCase(addAttachment.pending, (state, action) => {
            const alreadyRegistered = state.attachments.some((attachment) => attachment.file === action.meta.arg.file);
            if (!alreadyRegistered && action.meta.arg.file) {
                state.attachments.push({
                    file: action.meta.arg.file,
                    error: undefined,
                    isUploaded: false,
                });
            }
        });
        builder.addCase(addAttachment.rejected, (state, action) => {
            const file = state.attachments.find((attachment) => attachment.file === action.meta.arg.file);
            if (file) {
                file.isUploaded = false;
                if (action.payload === ErrorType.FILE_TOO_BIG) {
                    file.error = ErrorType.FILE_TOO_BIG;
                } else if (action.payload === ErrorType.WRONG_FILE_FORMAT) {
                    file.error = ErrorType.WRONG_FILE_FORMAT;
                } else {
                    file.error = ErrorType.UNKNOWN_ATTACHMENT_ERROR;
                }
            }
        });
        builder.addCase(deleteAttachmentAction.fulfilled, (state, action) => {
            state.attachments = state.attachments.filter((file) => file.id !== action.meta.arg.attachment_id);
        });
        builder.addCase(deleteAttachmentAction.pending, (state, action) => {
            const file = state.attachments.find((attachment) => attachment.id === action.meta.arg.attachment_id);
            if (file) {
                file.error = undefined;
                file.isUploaded = false;
            }
        });
        builder.addCase(deleteAttachmentAction.rejected, (state, action) => {
            const file = state.attachments.find((attachment) => attachment.id === action.meta.arg.attachment_id);
            if (file) {
                file.error = ErrorType.FILE_DELETION_FAILED;
                file.isUploaded = true;
            }
        });
    },
});

export const {removeFailedAttachment, registerAttachments} = slice.actions;

export default slice.reducer;
